import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  Divider
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Link as LinkIcon,
  PhotoCamera as PhotoCameraIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import { MacroImage, Annotation } from '../types';
import { getMacroImages, getMappingAnnotations } from '../../../services/moleMappingApi';

interface BaselineComparisonPanelProps {
  apiBaseUrl: string;
  baselineSessionId: string;
  followupSessionId: string;
  refreshTrigger?: number;
  currentSessionAsBaseline?: boolean;
  onSelectBaselineAnnotation: (annotation: Annotation, baselineImage: MacroImage) => void;
  onGoToDermatoscopic: () => void;
}

interface BaselineData {
  macroImages: MacroImage[];
  annotations: Annotation[];
  selectedImage: MacroImage | null;
}

interface ImageBounds {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
}

const BaselineComparisonPanel: React.FC<BaselineComparisonPanelProps> = ({
  apiBaseUrl,
  baselineSessionId,
  followupSessionId,
  refreshTrigger,
  currentSessionAsBaseline = false,
  onSelectBaselineAnnotation,
  onGoToDermatoscopic
}) => {
  const theme = useTheme();
  const [baselineData, setBaselineData] = useState<BaselineData>({
    macroImages: [],
    annotations: [],
    selectedImage: null
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBaselineAnnotation, setSelectedBaselineAnnotation] = useState<Annotation | null>(null);
  const [imageBounds, setImageBounds] = useState<ImageBounds | null>(null);
  const baselineImageRef = React.useRef<HTMLDivElement>(null);

  // Load baseline session data
  useEffect(() => {
    loadBaselineData();
  }, [baselineSessionId, refreshTrigger]);

  const loadBaselineData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load baseline macro images
      const macroImages = await getMacroImages(baselineSessionId);
      
      if (macroImages.length === 0) {
        setError("No baseline images found for comparison");
        return;
      }

      // Load annotations for the first baseline image
      const firstImage = macroImages[0];
      const annotations = await getMappingAnnotations(firstImage.id);

      setBaselineData({
        macroImages,
        annotations,
        selectedImage: firstImage
      });
    } catch (error) {
      console.error("Failed to load baseline data:", error);
      setError("Failed to load baseline session data");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBaselineImage = async (image: MacroImage) => {
  if (baselineData.selectedImage?.id === image.id) {
    // Image already selected, no need to re-fetch or reset
    return;
  }

  setLoading(true);
  try {
    const annotations = await getMappingAnnotations(image.id);
    setBaselineData(prev => ({
      ...prev,
      selectedImage: image,
      annotations
    }));
    setSelectedBaselineAnnotation(null);
    setImageBounds(null); // Reset bounds when switching images
  } catch (error) {
    console.error("Failed to load annotations for baseline image:", error);
    setError("Failed to load annotations for selected image");
  } finally {
    setLoading(false);
  }
};

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    const container = baselineImageRef.current;
    
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();
      
      setImageBounds({
        width: imgRect.width,
        height: imgRect.height,
        offsetX: imgRect.left - containerRect.left,
        offsetY: imgRect.top - containerRect.top
      });
    }
  };

  const recalculateImageBounds = () => {
    const container = baselineImageRef.current;
    if (container) {
      const img = container.querySelector('img');
      if (img) {
        const containerRect = container.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();
        
        setImageBounds({
          width: imgRect.width,
          height: imgRect.height,
          offsetX: imgRect.left - containerRect.left,
          offsetY: imgRect.top - containerRect.top
        });
      }
    }
  };

  // Add resize listener for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (baselineImageRef.current && baselineData.selectedImage) {
        recalculateImageBounds();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [baselineData.selectedImage]);

  const handleSelectAnnotation = (annotation: Annotation) => {
    setSelectedBaselineAnnotation(annotation);
  };

  const handleAddDermatoscopicToBaseline = () => {
    if (selectedBaselineAnnotation && baselineData.selectedImage) {
      // Call the parent handler to set up baseline context and switch tabs
      onSelectBaselineAnnotation(selectedBaselineAnnotation, baselineData.selectedImage);
    }
  };

  if (loading && baselineData.macroImages.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading baseline session data...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error && baselineData.macroImages.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompareArrowsIcon color="primary" />
            {currentSessionAsBaseline ? 'Current Session - Baseline Mode' : 'Baseline Session Comparison'}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {currentSessionAsBaseline 
              ? 'This session contains dermatoscopic images and can serve as a baseline. Select annotations to add follow-up images in future sessions.'
              : 'Select a baseline annotation to add follow-up dermatoscopic images. This allows tracking changes over time.'
            }
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Baseline macro images */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                {currentSessionAsBaseline ? 'Current Images' : 'Baseline Images'} ({baselineData.macroImages.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {baselineData.macroImages.map((image, index) => (
                  <Button
                    key={image.id}
                    variant={baselineData.selectedImage?.id === image.id ? "contained" : "outlined"}
                    onClick={() => handleSelectBaselineImage(image)}
                    sx={{ 
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      height: 60
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={`${apiBaseUrl}${image.imageUrl}`}
                        alt={`Baseline ${index + 1}`}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 4
                        }}
                      />
                      <Typography variant="body2">
                        {`Image ${index + 1}`}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Baseline image viewer */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {baselineData.selectedImage ? (
                <>
                {/* TO ENSURE ASPECT RATIO MAINTAINED
                Added display: 'flex', alignItems: 'center', justifyContent: 'center' to the container to center the image
            Changed the image styles to use maxWidth: '100%' and maxHeight: '100%' instead of width: '100%' and height: '100%'
              Added inline styles to the img element with width: 'auto' and height: 'auto' to preserve the natural aspect ratio */}

                  <Typography variant="subtitle1" gutterBottom>
                    {baselineData.selectedImage.imageName || 'Baseline Image'}
                  </Typography>
                <Box
                  ref={baselineImageRef}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 500,
                    bgcolor: 'background.paper',
                    overflow: 'auto',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {/* Wrapper for image and annotations that moves together */}
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      minWidth: 'fit-content'
                    }}
                  >
                    <img
                      src={`${apiBaseUrl}${baselineData.selectedImage.imageUrl}`}
                      alt="Baseline macro image"
                      onLoad={handleImageLoad}
                      style={{
                        height: '500px',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                    
                    {/* Baseline annotations overlay - now positioned relative to image wrapper */}
                    {baselineData.annotations.map(annotation => {
                      if (!imageBounds) return null;
                      if(annotation.linkedMicroId==null) return null;
                      return (
                        <Tooltip
                          key={annotation.id}
                          title={`${annotation.label}${annotation.linkedMicroId ? ' (Has dermatoscopic)' : ''}`}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: `${annotation.x * 100}%`,
                              top: `${annotation.y * 100}%`,
                              width: `${annotation.width * 100}%`,
                              height: `${annotation.height * 100}%`,
                              border: `2px solid ${
                                selectedBaselineAnnotation?.id === annotation.id 
                                  ? theme.palette.secondary.main
                                  : theme.palette.primary.main
                              }`,
                              backgroundColor: alpha(
                                selectedBaselineAnnotation?.id === annotation.id 
                                  ? theme.palette.secondary.main
                                  : theme.palette.primary.main, 
                                0.2
                              ),
                              cursor: 'pointer',
                              zIndex: 1,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.3)
                              }
                            }}
                            onClick={() => handleSelectAnnotation(annotation)}
                          >
                        
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -24,
                                left: 0,
                                backgroundColor: selectedBaselineAnnotation?.id === annotation.id 
                                  ? theme.palette.secondary.main
                                  : theme.palette.primary.main,
                                color: '#fff',
                                px: 1,
                                py: 0.5,
                                borderRadius: '4px 4px 0 0',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                zIndex: 2
                              }}
                            >
                              {annotation.label}
                              {annotation.linkedMicroId && (
                                <PhotoCameraIcon sx={{ fontSize: '0.75rem', ml: 0.5 }} />
                              )}
                            </Box>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
                </>
              ) : (
                <Typography color="text.secondary">
                  Select a baseline image to view annotations
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Baseline annotations list */}
      {baselineData.annotations.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Baseline Annotations ({baselineData.annotations.length})
            </Typography>
            <List dense>
            {baselineData.annotations
  .filter(a => a.linkedMicroId != null)
  .sort((a, b) => {
    const base = (label: string) => label.replace(/\s*\(Follow-up\)+/g, '').trim();
    const aBase = base(a.label);
    const bBase = base(b.label);

    if (aBase === bBase) {
      return a.label.length - b.label.length; // Put original before follow-ups
    }
    return aBase.localeCompare(bBase, undefined, { numeric: true });
  })
  .map(annotation => (
    <ListItem key={annotation.id} sx={{
      border: `1px solid ${
        selectedBaselineAnnotation?.id === annotation.id 
          ? theme.palette.secondary.main
          : theme.palette.divider
      }`,
      borderRadius: 1,
      mb: 1,
      backgroundColor: selectedBaselineAnnotation?.id === annotation.id 
        ? alpha(theme.palette.secondary.main, 0.1)
        : 'transparent'
    }}>
      <ListItemButton onClick={() => handleSelectAnnotation(annotation)}>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2">
                {annotation.label}
              </Typography>
              {annotation.linkedMicroId && (
                <Chip
                  size="small"
                  icon={<PhotoCameraIcon />}
                  label="Has Dermatoscopic"
                  color="info"
                  variant="outlined"
                />
              )}
              {selectedBaselineAnnotation?.id === annotation.id && (
                <Chip
                  size="small"
                  label="Selected"
                  color="secondary"
                  variant="filled"
                />
              )}
            </Box>
          }
          secondary={
            <Typography variant="body2" color="text.secondary">
              Position: ({Math.round(annotation.x * 100)}%, {Math.round(annotation.y * 100)}%) • 
              Size: {Math.round(annotation.width * 100)}% × {Math.round(annotation.height * 100)}%
            </Typography>
          }
        />
      </ListItemButton>
      {selectedBaselineAnnotation?.id === annotation.id && (
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip title="Add follow-up dermatoscopic image">
            <IconButton
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleAddDermatoscopicToBaseline();
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </ListItem>
))}
            </List>

            {selectedBaselineAnnotation && (
              <>
                <Divider sx={{ my: 2 }} />
                <Alert 
                  severity="info" 
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddDermatoscopicToBaseline}
                    >
                      Add Follow-up Image
                    </Button>
                  }
                >
                  Selected baseline annotation: <strong>{selectedBaselineAnnotation.label}</strong>
                  <br />
                  Click "Add Follow-up Image" to capture new dermatoscopic images for comparison with this 
                  baseline annotation. This will switch to the dermatoscopic view in follow-up mode.
                </Alert>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BaselineComparisonPanel;